import { AnalyticsUserEvent } from '@fruitful/types';
import { LinearGradient } from 'expo-linear-gradient';
import { router, useNavigation } from 'expo-router';
import { useAtom, useSetAtom } from 'jotai';
import * as React from 'react';
import { useEffect, useState } from 'react';
import {
    BackButton,
    BackButtonIconType,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FullScreenContainer,
    FullScreenSpinner,
    H5Emphasized,
    H5Regular,
    InfoButton,
    isWeb,
    PlusIcon,
    ScrollView,
    Separator,
    tokens,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';
import { v4 as uuidv4 } from 'uuid';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import Screens from '../../common/types/Screens';
import { sortCategories } from '../../common/utils/billUtils';
import LogRocketFacade from '../../logger/log-rocket/LogRocketFacade';
import { captureException, track } from '../../logger/Logger';
import useGetBillDiscoveryReview from '../hooks/useGetBillDiscoveryReview';
import { useGetAllBills } from '../hooks/useGetBills';
import useSubmitBillDiscoveryReview, {
    type SubmitBillDiscoveryReviewBody,
} from '../hooks/useSubmitBillDiscoveryReview';
import {
    billDiscoveryReviewAccountsAtom,
    billReviewSourceAtom,
    billsToReviewAtom,
    selectedReviewBillAtom,
    selectedReviewBillsAtom,
} from '../state/BillsAtoms';
import {
    BillActivationStatus,
    type IBill,
    type SelectableBill,
} from '../types/Bill';
import { BillRow } from './common/BillRows';
import { BillsTotal, BillsTotalView } from './common/BillsTotal';
import { EditableSelectableRow } from './common/EditableSelectableRow';

const BillsDiscoveryReview = () => {
    LogRocketFacade.tagPage('BillsDiscoveryReview');

    const toast = useToastController();
    const navigation = useNavigation();

    const user = useAuthedUser();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [billDiscoveryReviewAccounts, storeBillDiscoveryReviewAccounts] =
        useAtom(billDiscoveryReviewAccountsAtom);
    const [billsToReview, storeBillsToReview] = useAtom(billsToReviewAtom);

    const [selectedReviewBills, storeSelectedReviewBills] = useAtom(
        selectedReviewBillsAtom,
    );

    const [billReviewSource] = useAtom(billReviewSourceAtom);

    const storeSelectedBill = useSetAtom(selectedReviewBillAtom);

    const { mutate: submitReview } = useSubmitBillDiscoveryReview();

    const { refetchBills } = useGetAllBills();

    // Fetch bill discovery data if billsToReview is empty (direct navigation)
    const {
        data: fetchedBillDiscoveryReview,
        isLoading: isLoadingBillDiscoveryReview,
    } = useGetBillDiscoveryReview(user?.membership?.id ?? '');

    // Populate billsToReviewAtom if it's empty and we have data
    useEffect(() => {
        if (
            billsToReview.length === 0 &&
            fetchedBillDiscoveryReview.bills.length > 0 &&
            !isSubmitting
        ) {
            storeBillsToReview(fetchedBillDiscoveryReview.bills);
            storeBillDiscoveryReviewAccounts(
                fetchedBillDiscoveryReview.billAccounts,
            );
        }
    }, [
        billsToReview.length,
        fetchedBillDiscoveryReview.bills.length,
        fetchedBillDiscoveryReview.billAccounts.length,
        isSubmitting,
    ]);

    useEffect(() => {
        if (billsToReview.length && !isSubmitting) {
            storeSelectedReviewBills(
                billsToReview.map((b) => {
                    const existingBill = selectedReviewBills.find(
                        (s) => s.id === b.id,
                    );
                    return {
                        ...b,
                        isSelected: existingBill
                            ? existingBill.isSelected
                            : true,
                    } satisfies SelectableBill;
                }),
            );
        }
    }, [billsToReview, isSubmitting]);

    const toggleSelection = (bill: SelectableBill) => {
        storeSelectedReviewBills((prevBills) => {
            const updatedBills = [...prevBills];
            updatedBills.forEach((b) => {
                if (b.id === bill.id) {
                    b.isSelected = !b.isSelected;
                }
            });
            return updatedBills;
        });
    };

    const editBill = (bill: IBill) => {
        storeSelectedBill(bill);
        router.navigate('/bills/bill-review-details-new');
    };

    const onSubmit = () => {
        setIsSubmitting(true);

        const acceptedBills: IBill[] = [];
        const acceptedBillIds: string[] = [];
        const rejectedBillIds: string[] = [];

        selectedReviewBills.forEach((bill) => {
            if (bill.isSelected) {
                bill.activationStatus = BillActivationStatus.ACTIVE;
                acceptedBills.push(bill as IBill);
                acceptedBillIds.push(bill.id);
            } else {
                bill.activationStatus = BillActivationStatus.REJECTED;
                rejectedBillIds.push(bill.id);
            }
        });

        const reviewedAccountIds = billDiscoveryReviewAccounts
            .filter((a) => a.isSelectedForCurrentDiscovery)
            .map((a) => a.id);

        submitReview(
            {
                urlParams: {
                    membershipId: user?.membership?.id ?? '',
                },
                body: {
                    acceptedBillIds,
                    rejectedBillIds,
                    reviewedAccountIds,
                } satisfies SubmitBillDiscoveryReviewBody,
            },
            {
                onSuccess: () => {
                    // Update client state
                    storeBillsToReview([]);
                    refetchBills(true);

                    storeBillDiscoveryReviewAccounts((prevAccounts) => {
                        return prevAccounts.map((a) => {
                            if (a.isSelectedForCurrentDiscovery) {
                                a.isSelectedForCurrentDiscovery = false;
                                a.discoveryReviewCompletedAt = new Date();
                            }
                            return a;
                        });
                    });

                    if (billReviewSource === 'profile') {
                        router.navigate('/settings');
                    } else if (billReviewSource === 'onboarding') {
                        track(
                            AnalyticsUserEvent.ONBOARDING_FUNNEL_BILLS_CONFIRMED,
                            undefined,
                            true,
                        );
                        router.back();
                    } else if (acceptedBills.length > 0) {
                        // this ensures that when moving to the bills home screen, we remove the intermediate review screens from the stack but keep the home tab.
                        // that way when the member goes back, they'll go straight to the home tab.
                        // @ts-expect-error expo-router isn't happy with its own typing of the state arg?
                        navigation.dispatch((state) => {
                            const homeRoute = state.routes.find(
                                (route) => route.name === 'home',
                            );

                            const newRoutes = [
                                {
                                    key: uuidv4(),
                                    name: Screens.Bills.BillsHome as never,
                                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                                    params: {} as never,
                                },
                            ];
                            homeRoute && newRoutes.unshift(homeRoute);

                            navigation.reset({
                                ...state,
                                routes: newRoutes,
                                index: newRoutes.length - 1,
                            });
                        });
                    } else {
                        // @ts-expect-error expo-router isn't happy with its own typing of the state arg?
                        navigation.dispatch((state) => {
                            navigation.reset({
                                ...state,
                                routes: [
                                    {
                                        key: uuidv4(),
                                        name: Screens.Tabs.Home as never,
                                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                                        params: {} as never,
                                    },
                                ],
                                index: 0,
                            });
                        });
                    }

                    acceptedBills.length > 0 &&
                        toast.show(`${acceptedBills.length} bills added`);
                    setIsSubmitting(false);
                },
                onError: (error) => {
                    toast.show('Something went wrong');
                    captureException(error);
                    setIsSubmitting(false);
                    // TODO handle error UX
                },
            },
        );
    };

    const checkedBills = selectedReviewBills.filter((b) => b.isSelected);

    if (isSubmitting) {
        return <FullScreenSpinner />;
    }

    // Show loading spinner if we're fetching data for direct navigation
    if (billsToReview.length === 0 && isLoadingBillDiscoveryReview) {
        return <FullScreenSpinner />;
    }

    return (
        <FullScreenContainer
            header={
                <XStack
                    flex={1}
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <BackButton
                        fallbackRoute="/"
                        iconType={BackButtonIconType.ARROW_LEFT}
                        alignSelf="center"
                    />
                    <InfoButton
                        onPress={() => {
                            router.navigate({
                                pathname: '/bills/bills-category-info',
                            });
                        }}
                    />
                </XStack>
            }
            scrollDisabled={true}
        >
            <YStack
                flex={1}
                paddingVertical={isWeb ? 24 : undefined}
                alignSelf="center"
                justifyContent="space-between"
            >
                <YStack flex={1}>
                    <ScrollView showsVerticalScrollIndicator={isWeb}>
                        <YStack gap={12} paddingBottom={24}>
                            <H5Regular paddingTop={isWeb ? 0 : 16}>
                                Review and confirm that everything we detected
                                is accurate
                            </H5Regular>
                            <BodyMediumRegular color="$gray1000">
                                Add, update, or remove anything that isn’t a
                                bill.
                            </BodyMediumRegular>
                        </YStack>
                        <XStack
                            backgroundColor="$white"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <BillsTotal
                                bills={checkedBills}
                                viewType={BillsTotalView.DiscoveryReview}
                            />
                            <YStack
                                alignItems="flex-end"
                                justifyContent="space-between"
                                gap={4}
                                paddingVertical={16}
                            >
                                <H5Emphasized>
                                    {checkedBills.length} of{' '}
                                    {selectedReviewBills.length}
                                </H5Emphasized>
                                <BodyMediumRegular>Included</BodyMediumRegular>
                            </YStack>
                        </XStack>
                        <YStack alignItems="center">
                            {selectedReviewBills
                                .sort((a, b) => {
                                    return (
                                        sortCategories(
                                            a.category,
                                            b.category,
                                        ) || b.amount - a.amount
                                    );
                                })
                                .map((bill, i) => {
                                    return (
                                        <XStack key={i}>
                                            <EditableSelectableRow
                                                isSelected={bill.isSelected}
                                                isMultiSelect={true}
                                                isFirstRow={i === 0}
                                                rowContentComponent={
                                                    <BillRow
                                                        bill={bill as IBill}
                                                        isSelected={
                                                            bill.isSelected
                                                        }
                                                        onSelect={() => {
                                                            toggleSelection(
                                                                bill,
                                                            );
                                                        }}
                                                    />
                                                }
                                                onEdit={() => {
                                                    editBill(bill as IBill);
                                                }}
                                            />
                                        </XStack>
                                    );
                                })}
                            <YStack paddingVertical={24} width="100%">
                                <Button
                                    variant={ButtonVariant.SECONDARY}
                                    icon={PlusIcon}
                                    text="Add a new bill"
                                    onPress={() => {
                                        router.navigate(
                                            '/bills/bill-review-add-new',
                                        );
                                    }}
                                    size={ButtonSize.LARGE}
                                />
                            </YStack>
                        </YStack>
                    </ScrollView>
                    <LinearGradient
                        colors={[
                            'rgba(255, 255, 255, 0)',
                            tokens.color.light.brand.white,
                        ]}
                        style={{
                            height: 20,
                            width: '100%',
                            position: 'absolute',
                            bottom: 0,
                        }}
                        pointerEvents="none"
                    />
                </YStack>
                <Separator />
                <YStack paddingTop={16}>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Continue"
                        onPress={onSubmit}
                        disabled={billsToReview.length < 1}
                    />
                </YStack>
            </YStack>
        </FullScreenContainer>
    );
};

export default BillsDiscoveryReview;
